import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

function ImageField({ label, preview, existing, onChange, disabled }) {
  return (
    <div className="col-md-6">
      <label className="h5 mb-8 fw-semibold font-heading d-block">{label}</label>
      <div className="d-flex align-items-center gap-16">
        {(preview || existing) ? (
          <img
            src={preview || `${apiUrl}/${existing}`}
            alt="preview"
            style={{ width: 120, height: 90, borderRadius: 12, objectFit: 'cover', border: '3px solid #e8f5ee' }}
          />
        ) : (
          <div style={{
            width: 120, height: 90, borderRadius: 12, background: '#f3f4f6',
            display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed #d1d5db'
          }}>
            <i className="ph ph-image text-gray-400" style={{ fontSize: 28 }} />
          </div>
        )}
        <input type="file" className="form-control py-11" accept="image/*" onChange={onChange} disabled={disabled} />
      </div>
    </div>
  );
}

function AdminHomeContent() {
  const [form, setForm] = useState({
    marquee_text: '',
    about_subtitle: '',
    about_title: '',
    about_body: '',
    strategic_subtitle: '',
    strategic_title: '',
    strategic_button_text: '',
    strategic_button_link: '',
    strategic_panel1_title: '',
    strategic_panel1_desc: '',
    strategic_panel2_title: '',
    strategic_panel2_desc: '',
  });
  const [images, setImages] = useState({ about_image1: null, about_image2: null, strategic_image: null });
  const [previews, setPreviews] = useState({ about_image1: null, about_image2: null, strategic_image: null });
  const [existing, setExisting] = useState({ about_image1: '', about_image2: '', strategic_image: '' });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const getToken = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    return session?.token || null;
  };

  const fetchContent = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) {
        setError('Authentication required.');
        setLoading(false);
        return;
      }
      const response = await axios.get(`${apiUrl}/api/admin/home-content`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        const c = response.data.content || {};
        setForm((prev) => ({
          ...prev,
          marquee_text: c.marquee_text || '',
          about_subtitle: c.about_subtitle || '',
          about_title: c.about_title || '',
          about_body: c.about_body || '',
          strategic_subtitle: c.strategic_subtitle || '',
          strategic_title: c.strategic_title || '',
          strategic_button_text: c.strategic_button_text || '',
          strategic_button_link: c.strategic_button_link || '',
          strategic_panel1_title: c.strategic_panel1_title || '',
          strategic_panel1_desc: c.strategic_panel1_desc || '',
          strategic_panel2_title: c.strategic_panel2_title || '',
          strategic_panel2_desc: c.strategic_panel2_desc || '',
        }));
        setExisting({
          about_image1: c.about_image1 || '',
          about_image2: c.about_image2 || '',
          strategic_image: c.strategic_image || '',
        });
      } else {
        setError(response.data.message || 'Failed to fetch homepage content.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching homepage content.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const handleImageChange = (key) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImages((prev) => ({ ...prev, [key]: file }));
    const reader = new FileReader();
    reader.onload = (ev) => setPreviews((prev) => ({ ...prev, [key]: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => formData.append(key, value));
    Object.entries(images).forEach(([key, file]) => {
      if (file) formData.append(key, file);
    });

    try {
      const token = getToken();
      const response = await axios.put(`${apiUrl}/api/admin/home-content`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success) {
        setSuccess('Homepage content updated successfully.');
        setImages({ about_image1: null, about_image2: null, strategic_image: null });
        setPreviews({ about_image1: null, about_image2: null, strategic_image: null });
        fetchContent();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message || 'Failed to save.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">Homepage Content</span></li>
              </ul>
            </div>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          {loading ? (
            <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="card mb-24">
                <div className="card-header border-bottom border-gray-100">
                  <h5 className="mb-0">Marquee</h5>
                  <span className="text-13 text-gray-400 d-block mt-4">The scrolling text banner between the hero and the About section.</span>
                </div>
                <div className="card-body">
                  <input
                    type="text"
                    className="form-control py-11"
                    value={form.marquee_text}
                    onChange={(e) => setField('marquee_text', e.target.value)}
                    disabled={saving}
                    placeholder="e.g. We don't just teach, We transform careers..."
                  />
                </div>
              </div>

              <div className="card mb-24">
                <div className="card-header border-bottom border-gray-100">
                  <h5 className="mb-0">About Us (Homepage)</h5>
                </div>
                <div className="card-body">
                  <div className="row g-20">
                    <ImageField
                      label="Image 1"
                      preview={previews.about_image1}
                      existing={existing.about_image1}
                      onChange={handleImageChange('about_image1')}
                      disabled={saving}
                    />
                    <ImageField
                      label="Image 2"
                      preview={previews.about_image2}
                      existing={existing.about_image2}
                      onChange={handleImageChange('about_image2')}
                      disabled={saving}
                    />
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Subtitle</label>
                      <input
                        type="text"
                        className="form-control py-11"
                        value={form.about_subtitle}
                        onChange={(e) => setField('about_subtitle', e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Title</label>
                      <input
                        type="text"
                        className="form-control py-11"
                        value={form.about_title}
                        onChange={(e) => setField('about_title', e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="col-12">
                      <label className="h5 mb-8 fw-semibold font-heading d-block">Body</label>
                      <CKEditor
                        editor={ClassicEditor}
                        data={form.about_body}
                        onChange={(event, editor) => setField('about_body', editor.getData())}
                        config={{ toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'undo', 'redo'] }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card mb-24">
                <div className="card-header border-bottom border-gray-100">
                  <h5 className="mb-0">Strategic / Info Section</h5>
                  <span className="text-13 text-gray-400 d-block mt-4">The "Strategic Learning Paths" block with two side-by-side panels.</span>
                </div>
                <div className="card-body">
                  <div className="row g-20">
                    <ImageField
                      label="Background Image"
                      preview={previews.strategic_image}
                      existing={existing.strategic_image}
                      onChange={handleImageChange('strategic_image')}
                      disabled={saving}
                    />
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Subtitle</label>
                      <input
                        type="text"
                        className="form-control py-11"
                        value={form.strategic_subtitle}
                        onChange={(e) => setField('strategic_subtitle', e.target.value)}
                        disabled={saving}
                      />
                      <div className="mt-16">
                        <label className="h5 mb-8 fw-semibold font-heading">Title</label>
                        <input
                          type="text"
                          className="form-control py-11"
                          value={form.strategic_title}
                          onChange={(e) => setField('strategic_title', e.target.value)}
                          disabled={saving}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Button Text</label>
                      <input
                        type="text"
                        className="form-control py-11"
                        value={form.strategic_button_text}
                        onChange={(e) => setField('strategic_button_text', e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Button Link</label>
                      <input
                        type="text"
                        className="form-control py-11"
                        value={form.strategic_button_link}
                        onChange={(e) => setField('strategic_button_link', e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Panel 1 Title</label>
                      <input
                        type="text"
                        className="form-control py-11"
                        value={form.strategic_panel1_title}
                        onChange={(e) => setField('strategic_panel1_title', e.target.value)}
                        disabled={saving}
                      />
                      <div className="mt-16">
                        <label className="h5 mb-8 fw-semibold font-heading">Panel 1 Description</label>
                        <textarea
                          className="form-control py-11"
                          rows={3}
                          value={form.strategic_panel1_desc}
                          onChange={(e) => setField('strategic_panel1_desc', e.target.value)}
                          disabled={saving}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Panel 2 Title</label>
                      <input
                        type="text"
                        className="form-control py-11"
                        value={form.strategic_panel2_title}
                        onChange={(e) => setField('strategic_panel2_title', e.target.value)}
                        disabled={saving}
                      />
                      <div className="mt-16">
                        <label className="h5 mb-8 fw-semibold font-heading">Panel 2 Description</label>
                        <textarea
                          className="form-control py-11"
                          rows={3}
                          value={form.strategic_panel2_desc}
                          onChange={(e) => setField('strategic_panel2_desc', e.target.value)}
                          disabled={saving}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-end gap-8 mb-24">
                <button type="submit" className="btn btn-main rounded-pill py-9" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminHomeContent;
